import { styled } from '@mui/material/styles';
import { Box, Button } from '@mui/material';

export const MainContent = styled(Box)<{ sidebarOpen?: boolean }>(({ theme, sidebarOpen = true }) => ({
  position: 'relative',
  right: 0,
  top: 0,
  width: '100%',
  padding: theme.spacing(3),
  transition: 'all 0.3s ease',
  backgroundColor: '#E7EFF5',
  height: '100%',
  minHeight: 'calc(100vh - 72px)',
  maxHeight: '100%',
  overflowX: 'hidden',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  boxSizing: 'border-box',
  '& .MuiContainer-root': {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

export const ReportContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  padding: theme.spacing(2),
  maxWidth: '100%',
  margin: '0 auto',
  width: '100%',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

export const FilterSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '4px',
  backgroundColor: '#FFFFFF',
}));

export const SelectContainer = styled(Box)(({ theme }) => ({
  minWidth: '120px',
  '& .MuiSelect-select': {
    fontSize: '14px',
    padding: '8px 14px',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    height: '32px',
    '& fieldset': {
      borderColor: '#D1D9E0',
    },
    '&:hover fieldset': {
      borderColor: '#00ACE7',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00ACE7',
    },
  },
}));

export const SectionContainer = styled(Box)(({ theme }) => ({
  borderRadius: '4px',
  padding: theme.spacing(1.5),
  border: '1px solid #E1E8ED',
  height: 'fit-content',
}));

export const SectionTitle = styled(Box)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#063142',
  fontFamily: '"Lato-Regular", sans-serif',
}));

export const CheckboxGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
}));

export const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: '70px',
  height: '28px',
  fontSize: '11px',
  fontWeight: 600,
  fontFamily: '"Lato-Regular", sans-serif',
  textTransform: 'uppercase',
  borderRadius: '4px',
  padding: '4px 12px',
  '&.MuiButton-contained': {
    backgroundColor: '#00ACE7',
    color: '#FFFFFF',
    border: 'none',
    '&:hover': {
      backgroundColor: '#0095C7',
    },
  },
  '&.MuiButton-outlined': {
    borderColor: '#D1D9E0',
    color: '#063142',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      borderColor: '#00ACE7',
      backgroundColor: '#F8F9FA',
    },
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const StyledSegmentedControl = styled(Box)(({ theme }) => ({
  borderRadius: '4px',
  border: '1px solid #00B6BA',
  display: 'flex',
  flexDirection: 'row',
  gap: '0px',
  alignItems: 'center',
  justifyContent: 'center',
  height: '32px',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    flex: '1 0 auto'
  }
}));

export const StyledSegmentButton = styled(Button)<{ active?: boolean }>(({ active, theme }) => ({
  '&.MuiButton-root.MuiButton-root': {
    border: '1px solid #00B6BA',
    padding: '6px 12px',
    height: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: `${active ? '#00B6BA !important' : 'transparent !important'}`,
    color: active ? '#ffffff !important' : '#00B6BA !important',
    borderRadius: 0,
    minWidth: '70px',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: `${active ? '#00B6BA !important' : 'rgba(0, 182, 186, 0.1) !important'}`,
      color: active ? '#ffffff !important' : '#00B6BA !important',
    },
    '&:focus': {
      outline: 'none',
      color: active ? '#ffffff !important' : '#00B6BA !important',
    }
  },
  '&:last-child': {
    borderRight: 'none'
  }
}));

// Clear Results Button
export const ClearResultsButton = styled(Button)(({ theme }) => ({
  color: '#FF5722',
  borderColor: '#FF5722',
  fontSize: '12px',
  '&:hover': {
    borderColor: '#D84315',
    backgroundColor: '#fff3e0'
  }
}));

// Reset Button
export const ResetButton = styled(Button)(({ theme }) => ({
  height: '30px',
  borderRadius: '1000px',
  padding: '0 24px',
  backgroundColor: '#E7EFF5',
  color: '#063142',
  border: '1px solid #D1D9E0',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  minWidth: 'auto',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#D1D9E0',
    borderColor: '#B8C4CE',
    boxShadow: 'none',
  }
}));

// Export Button (HTML, PDF, XLS)
export const ExportButton = styled(Button)(({ theme }) => ({
  height: '30px',
  borderRadius: '1000px',
  padding: '0 24px',
  backgroundColor: '#00ACE7',
  color: '#FFFFFF',
  border: 'none',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  minWidth: 'auto',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#0095C7',
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: '#B8C4CE',
    color: '#6C757D',
    boxShadow: 'none',
  }
})); 